import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { all_routes } from "../../../router/all_routes";
import CollapseHeader from "../../../../core/common/collapse-header/collapse-header";
import Datatable from "../../../../core/common/dataTable";
import { ColumnsType } from "antd/es/table";
import { getSearchableHeader } from "../../../utils/getSearchableHeader";
import AssignPackage from "./AssignPackage";
import { cellWidth } from "../../../../core/methods/tableCellWidth";
import Footer from "../../../../core/common/Footer";
import { displayValue } from "../../../../core/common/TabelValue";
import useApi from "../../../../core/customhook/useApi";
import Swal from "sweetalert2";
import SnipperLoader from "../../../../core/common/loader/SnipperLoader";
import AssignBulkPackage from "./AssignBulkPackage";

const Package = () => {
  const [showGuide, setShowGuide] = useState<boolean>(true);
  const [assignPackage, setAssignPackage] = useState<boolean>(false);
  const [assignBulkPackage, setAssignBulkPackage] = useState<boolean>(false);
  const navigate = useNavigate();
  const [itemList, setItemList] = useState<any[]>([]);
  const [columnSearch, setColumnSearch] = useState<Record<string, string>>({});
  const [pagination, setPagination] = useState({ page: 1, limit: 1000 });
  const [total, setTotal] = useState<number>(0);

  const getPackageList = useApi<any>(
    `/api/v1/admin/package-hierarchy?page=${pagination.page}&limit=${pagination.limit}`,
    {
      method: "GET",
      autoCall: false,
      handleGlobalError: false,
    },
  );

  const updatePackageDepartmentStatus = useApi<any>("", {
    method: "PATCH",
    autoCall: false,
    handleGlobalError: false,
  });

  const packageLoading = getPackageList.loading;

  const loadPackageList = async () => {
    try {
      const res = await getPackageList.callApi();

      if (res?.success) {
        const data = res?.data;
        const rows = Array.isArray(data)
          ? data
          : (data?.rows ?? data?.table ?? []);
        setItemList(rows);

        const pg = data?.pagination;
        setTotal(
          pg?.total ??
            pg?.totalRecords ??
            (Array.isArray(data) ? data.length : 0),
        );
      } else {
        setItemList([]);
        setTotal(0);
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Unable to load package list.",
      });
    }
  };

  useEffect(() => {
    loadPackageList();
  }, [pagination.page, pagination.limit]);

  const filteredData = useMemo(() => {
    return itemList.filter((row) =>
      Object.entries(columnSearch).every(([key, value]) =>
        value
          ? row[key as keyof typeof row]
              ?.toString()
              .toLowerCase()
              .includes(value.toLowerCase())
          : true,
      ),
    );
  }, [itemList, columnSearch]);

  const handleStatusToggle = async (record: any) => {
    const previousStatus = record.is_active;
    setItemList((prev: any[]) =>
      prev.map((item) =>
        item.pkg_department_id === record.pkg_department_id
          ? { ...item, is_active: !previousStatus }
          : item,
      ),
    );

    try {
      const payload = { is_active: !previousStatus };

      const res = await updatePackageDepartmentStatus.callApi(
        payload,
        `/api/v1/admin/packages/assign/departments/${record.pkg_department_id}/status`,
      );

      if (!res?.success) {
        throw new Error(res?.message);
      }
    } catch (error: any) {
      // Revert UI
      setItemList((prev: any[]) =>
        prev.map((item) =>
          item.pkg_department_id === record.pkg_department_id
            ? { ...item, is_active: previousStatus }
            : item,
        ),
      );

      Swal.fire({
        icon: "error",
        title: "Update Failed",
        text:
          error?.response?.data?.message ||
          error?.message ||
          "Unable to update package status.",
      });
    }
  };

  const handleTableChange = (pgConfig: any) => {
    const newPage = pgConfig.current;
    const newLimit = pgConfig.pageSize;

    if (newPage === pagination.page && newLimit === pagination.limit) return;

    setPagination({
      // page size badalne par first page par jao
      page: newLimit !== pagination.limit ? 1 : newPage,
      limit: newLimit,
    });
  };

  // ---- Columns ----
  const columns: ColumnsType<any> = [
    {
      title: "Sr. No.",
      key: "serial_number",
      width: 80,
      align: "center",
      render: (_: any, __: any, index: number) =>
        (pagination.page - 1) * pagination.limit + index + 1,
    },
    {
      title: getSearchableHeader("Account", "project_name", setColumnSearch),
      dataIndex: "project_name",
      key: "project_name",
      sorter: (a, b) =>
        (a.project_name ?? "").localeCompare(b.project_name ?? ""),
      onCell: () => cellWidth(200),
      onHeaderCell: () => ({ style: { whiteSpace: "nowrap" } }),
      render: displayValue,
    },
    {
      title: getSearchableHeader("Program", "program_name", setColumnSearch),
      dataIndex: "program_name",
      key: "program_name",
      sorter: (a, b) =>
        (a.program_name ?? "").localeCompare(b.program_name ?? ""),
      onCell: () => cellWidth(200),
      onHeaderCell: () => ({ style: { whiteSpace: "nowrap" } }),
      render: displayValue,
    },
    {
      title: getSearchableHeader(
        "Department",
        "department_name",
        setColumnSearch,
      ),
      dataIndex: "department_name",
      key: "department_name",
      sorter: (a, b) =>
        (a.department_name ?? "").localeCompare(b.department_name ?? ""),
      onCell: () => cellWidth(200),
      onHeaderCell: () => ({ style: { whiteSpace: "nowrap" } }),
      render: displayValue,
    },
    {
      title: getSearchableHeader(
        "Package Name",
        "package_name",
        setColumnSearch,
      ),
      dataIndex: "package_name",
      key: "package_name",
      sorter: (a, b) =>
        (a.package_name ?? "").localeCompare(b.package_name ?? ""),
      onHeaderCell: () => ({ style: { whiteSpace: "nowrap" } }),
      onCell: () => cellWidth(300),
      render: displayValue,
    },
    {
      title: getSearchableHeader("Code", "package_code", setColumnSearch),
      dataIndex: "package_code",
      key: "package_code",
      align:"center",
      onCell: () => cellWidth(200),
      onHeaderCell: () => ({ style: { whiteSpace: "nowrap" } }),
      render: displayValue,
    },
    {
      title: getSearchableHeader("Check Name", "sop", setColumnSearch),
      dataIndex: "sop",
      key: "sop",
      align:"center",
      onCell: () => cellWidth(500),
      render: displayValue,
    },
    {
      title: "Active/Inactive",
      dataIndex: "is_active",
      key: "is_active",
      align: "center",
      render: (_: any, record: any) => (
        <div className="form-check form-switch d-flex justify-content-center">
          <input
            className="form-check-input"
            type="checkbox"
            style={{cursor:"pointer"}}
            checked={!!record.is_active}
            onChange={() => handleStatusToggle(record)}
          />
        </div>
      ),
    },
  ];

  return (
    <div className="page-wrapper">
      <div className="content">
        {/* Breadcrumb */}
        <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
          <div className="my-auto mb-2">
            <h2 className="mb-1">Manage Packages</h2>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={all_routes.superAdminDashboard}>
                    <i className="ti ti-smart-home" />
                  </Link>
                </li>
                <li className="breadcrumb-item">Account Activation</li>
                <li className="breadcrumb-item">Account Configuration</li>
                <li className="breadcrumb-item active">Manage Packages</li>
              </ol>
            </nav>
          </div>
          <div className="head-icons ms-2">
            <CollapseHeader />
          </div>
        </div>

        <div className="d-flex justify-content-end gap-2 align-items-center mt-3">
          <button
            className="btn btn-primary"
            onClick={() => navigate(all_routes.createPackage)}
          >
            <i className="ti ti-plus"></i> Create Package
          </button>
          <button
            className="btn btn-primary"
            onClick={() => navigate(all_routes.allPackage)}
          >
            <i className="ti ti-plus"></i> Manage Packages
          </button>
          <button
            className="btn btn-primary"
            onClick={() => setAssignPackage(true)}
          >
            <i className="ti ti-plus"></i> Assign Package
          </button>
          <button
            className="btn btn-primary"
            onClick={() => setAssignBulkPackage(true)}
          >
            <i className="ti ti-plus"></i> Bulk Assign Packages
          </button>
          <button
            className="btn border bg-white px-3"
            onClick={() => setShowGuide((prev) => !prev)}
          >
            <i className="ti ti-info-circle-filled fs-16"></i> Guide
          </button>
        </div>

        {showGuide && (
          <div
            style={{
              overflow: "hidden",
              maxHeight: showGuide ? "500px" : "0px",
              transition: "all 0.4s ease",
            }}
          >
            {/* Top Arrow */}
            <div className="text-end pe-3">
              <i
                className="ti ti-triangle-inverted-filled"
                style={{
                  fontSize: "22px",
                  color: "#cfcfcf",
                  lineHeight: 0,
                }}
              ></i>
            </div>

            {/* Guide Box */}
            <div
              className="card border-0 shadow-sm"
              style={{ borderRadius: "4px" }}
            >
              <div className="card-body position-relative p-4">
                {/* Close Button */}
                <div className="position-absolute top-0 end-0 mt-3 me-3">
                  <i
                    className="ti ti-x"
                    style={{
                      background: "#6c757d",
                      color: "#fff",
                      borderRadius: "50%",
                      width: "20px",
                      height: "20px",
                      fontSize: "12px",
                      cursor: "pointer",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                    onClick={() => setShowGuide(false)}
                  ></i>
                </div>

                {/* Heading */}
                <h5 className="fw-bold mb-3">Manage Packages</h5>

                <ul
                  className="mb-0 ps-3 text-dark"
                  style={{ lineHeight: "1.8", listStyleType: "disc" }}
                >
                  <li>
                    Create and manage background verification packages based on
                    different job roles, programs, or business requirements.
                    Each package contains a defined set of screening checks to
                    ensure a consistent verification process.
                  </li>
                  <li>
                    For example, you can create separate packages for Operations
                    Executives and Operations Managers, with checks such as
                    Employment Verification, Address Verification, and Identity
                    Verification.
                  </li>
                  <li>
                    Assign packages to the appropriate programs or lines of
                    business. You can also assign packages in bulk when
                    required.
                  </li>
                </ul>
                {/* Paragraph */}
                {/* <p className="mb-3 text-muted" style={{ lineHeight: "1.7" }}>
                  This allows you to give an easy name to each package that you
                  may want to order. For example within operations you may want
                  to add a package name as Operations Executive, Operations
                  Manager. You can also assign the scope of work for that
                  package. For example you want to conduct 2Employment +
                  1Address + 1ID Check for the operations executive.
                </p> */}
              </div>
            </div>
          </div>
        )}

        {assignPackage && (
          <AssignPackage
            show={assignPackage}
            handleClose={() => setAssignPackage(false)}
            onSuccess={loadPackageList}
          />
        )}
        {assignBulkPackage && (
          <AssignBulkPackage
            show={assignBulkPackage}
            handleClose={() => setAssignBulkPackage(false)}
            onSuccess={loadPackageList}
          />
        )}

        {packageLoading && itemList.length === 0 ? (
          <SnipperLoader />
        ) : (
          <div className="card mt-3">
            <Datatable
              fixedHeader
              tableHeight={650}
              rowKey="pkg_department_id"
              dataSource={filteredData}
              columns={columns}
              Selection={false}
              pagination={{
                current: pagination.page,
                pageSize: pagination.limit,
                total,
              }}
              handleTableChange={handleTableChange}
            />
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default Package;

// import React, { useEffect, useMemo, useState } from "react";
// import { Link, useNavigate } from "react-router-dom";
// import { all_routes } from "../../../router/all_routes";
// import CollapseHeader from "../../../../core/common/collapse-header/collapse-header";
// import Datatable from "../../../../core/common/dataTable";
// import { ColumnsType } from "antd/es/table";
// import { getSearchableHeader } from "../../../utils/getSearchableHeader";
// import AssignPackage from "./AssignPackage";
// import { cellWidth } from "../../../../core/methods/tableCellWidth";
// import Footer from "../../../../core/common/Footer";
// import { displayValue } from "../../../../core/common/TabelValue";
// import useApi from "../../../../core/customhook/useApi";
// import Swal from "sweetalert2";
// import SnipperLoader from "../../../../core/common/loader/SnipperLoader";
// import AssignBulkPackage from "./AssignBulkPackage";

// const Package = () => {
//   const [showGuide, setShowGuide] = useState<boolean>(true);
//   const [assignPackage, setAssignPackage] = useState<boolean>(false);
//   const [assignBulkPackage, setAssignBulkPackage] = useState<boolean>(false);
//   const navigate = useNavigate();
//   const [itemList, setItemList] = useState<any[]>([]);
//   const [pagination, setPagination] = useState({ page: 1, limit: 10 });
//   const [total, setTotal] = useState<number>(0);
//   const [columnSearch, setColumnSearch] = useState<Record<string, string>>({});

//   const getPackageList = useApi<any>("/api/v1/admin/package-hierarchy", {
//     method: "GET",
//     autoCall: false,
//     handleGlobalError: false,
//   });

//   const updatePackageDepartmentStatus = useApi<any>("", {
//     method: "PATCH",
//     autoCall: false,
//     handleGlobalError: false,
//   });

//   const packageLoading = getPackageList.loading;

//   // const loadPackageList = async () => {
//   //   try {
//   //     const res = await getPackageList.callApi();

//   //     if (res?.success) {
//   //       setItemList(res.data);
//   //     }
//   //   } catch (error) {
//   //     Swal.fire({
//   //       icon: "error",
//   //       title: "Error",
//   //       text: "Unable to load package list.",
//   //     });
//   //   }
//   // };

//   const loadPackageList = async () => {
//     try {
//       const res = await getPackageList.callApi();

//       if (res?.success) {
//         setItemList(res.data);
//       }
//     } catch (error) {
//       Swal.fire({
//         icon: "error",
//         title: "Error",
//         text: "Unable to load package list.",
//       });
//     }
//   };

//   useEffect(() => {
//     loadPackageList();
//   }, []);

//   // ---- Filtered Data ----
//   const filteredData = useMemo(() => {
//     return itemList.filter((row) =>
//       Object.entries(columnSearch).every(([key, value]) =>
//         value
//           ? row[key as keyof typeof row]
//               ?.toString()
//               .toLowerCase()
//               .includes(value.toLowerCase())
//           : true,
//       ),
//     );
//   }, [itemList, columnSearch]);

//   const handleStatusToggle = async (record: any) => {
//     const previousStatus = record.is_active;
//     setItemList((prev: any[]) =>
//       prev.map((item) =>
//         item.pkg_department_id === record.pkg_department_id
//           ? {
//               ...item,
//               is_active: !previousStatus,
//             }
//           : item,
//       ),
//     );

//     try {
//       const payload = {
//         is_active: !previousStatus,
//       };

//       const res = await updatePackageDepartmentStatus.callApi(
//         payload,
//         `/api/v1/admin/packages/assign/departments/${record.pkg_department_id}/status`,
//       );

//       if (!res?.success) {
//         throw new Error(res?.message);
//       }
//     } catch (error: any) {
//       // Revert UI
//       setItemList((prev: any[]) =>
//         prev.map((item) =>
//           item.pkg_department_id === record.pkg_department_id
//             ? {
//                 ...item,
//                 is_active: previousStatus,
//               }
//             : item,
//         ),
//       );

//       Swal.fire({
//         icon: "error",
//         title: "Update Failed",
//         text:
//           error?.response?.data?.message ||
//           error?.message ||
//           "Unable to update package status.",
//       });
//     }
//   };
//   // ---- Columns ----
//   const columns: ColumnsType<any> = [
//     {
//       title: "Sr. No.",
//       key: "serial_number",
//       dataIndex: "serial_number",
//       width: 80,
//       align: "center",
//     },
//     {
//       title: getSearchableHeader("Account", "project_name", setColumnSearch),
//       dataIndex: "project_name",
//       key: "project_name",
//       sorter: (a, b) => a.project_name.localeCompare(b.project_name),
//       onCell: () => cellWidth(180),
//       onHeaderCell: () => ({
//         style: {
//           whiteSpace: "nowrap",
//         },
//       }),
//       render: displayValue,
//     },
//     {
//       title: getSearchableHeader("Program", "program_name", setColumnSearch),
//       dataIndex: "program_name",
//       key: "program_name",
//       sorter: (a, b) => a.program_name.localeCompare(b.program_name),
//       onCell: () => cellWidth(300),
//       onHeaderCell: () => ({
//         style: {
//           whiteSpace: "nowrap",
//         },
//       }),
//       render: displayValue,
//     },

//     {
//       title: getSearchableHeader(
//         "Department",
//         "department_name",
//         setColumnSearch,
//       ),
//       dataIndex: "department_name",
//       key: "department_name",
//       sorter: (a, b) => a.department_name.localeCompare(b.department_name),
//       onCell: () => cellWidth(180),
//       onHeaderCell: () => ({
//         style: {
//           whiteSpace: "nowrap",
//         },
//       }),
//       render: displayValue,
//     },

//     {
//       title: getSearchableHeader(
//         "Package Name",
//         "package_name",
//         setColumnSearch,
//       ),
//       dataIndex: "package_name",
//       key: "package_name",
//       sorter: (a, b) => a.package_name.localeCompare(b.package_name),
//       onHeaderCell: () => ({
//         style: {
//           whiteSpace: "nowrap",
//         },
//       }),
//       onCell: () => cellWidth(300),
//       render: displayValue,
//     },

//     {
//       title: getSearchableHeader("Code", "package_code", setColumnSearch),
//       dataIndex: "package_code",
//       key: "package_code",
//       onCell: () => cellWidth(180),
//       onHeaderCell: () => ({
//         style: {
//           whiteSpace: "nowrap",
//         },
//       }),
//       render: displayValue,
//     },

//     {
//       title: getSearchableHeader("Check Name", "sop", setColumnSearch),
//       dataIndex: "sop",
//       key: "sop",
//       onCell: () => cellWidth(380),
//       render: displayValue,
//     },

//     {
//       title: "Active/Inactive",
//       dataIndex: "is_active",
//       key: "is_active",
//       align: "center",
//       render: (_: any, record: any) => (
//         <div className="form-check form-switch d-flex justify-content-center">
//           <input
//             className="form-check-input"
//             type="checkbox"
//             checked={record.is_active}
//             onChange={() => handleStatusToggle(record)}
//           />
//         </div>
//       ),
//     },
//   ];

//     const handleTableChange = (pgConfig: any) => {
//     const newPage = pgConfig.current;
//     const newLimit = pgConfig.pageSize;

//     if (newPage === pagination.page && newLimit === pagination.limit) return;

//     setPagination({
//       // page size badalne par first page par jao
//       page: newLimit !== pagination.limit ? 1 : newPage,
//       limit: newLimit,
//     });
//   };

//   return (
//     <div className="page-wrapper">
//       <div className="content">
//         {/* Breadcrumb */}
//         <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
//           <div className="my-auto mb-2">
//             <h2 className="mb-1">Manage Packages</h2>
//             <nav>
//               <ol className="breadcrumb mb-0">
//                 <li className="breadcrumb-item">
//                   <Link to={all_routes.superAdminDashboard}>
//                     <i className="ti ti-smart-home" />
//                   </Link>
//                 </li>
//                 <li className="breadcrumb-item">Account Activation</li>
//                 <li className="breadcrumb-item">Account Configuration</li>
//                 <li className="breadcrumb-item active">Manage Packages</li>
//               </ol>
//             </nav>
//           </div>
//           <div className="head-icons ms-2">
//             <CollapseHeader />
//           </div>
//         </div>
//         {/* <GuideContainer
//           showGuide={showGuide}
//           handleClick={() => setShowGuide((prev) => !prev)}
//         /> */}

//         <div className="d-flex justify-content-end gap-2 align-items-center mt-3">
//           <button
//             className="btn btn-primary"
//             onClick={() => navigate(all_routes.createPackage)}
//           >
//             <i className="ti ti-plus"></i> Create Package
//           </button>
//           <button
//             className="btn btn-primary"
//             onClick={() => navigate(all_routes.allPackage)}
//           >
//             <i className="ti ti-plus"></i> Manage Packages
//           </button>
//           <button
//             className="btn btn-primary"
//             onClick={() => setAssignPackage(true)}
//           >
//             <i className="ti ti-plus"></i> Assign Package
//           </button>
//           <button
//             className="btn btn-primary"
//             onClick={() => setAssignBulkPackage(true)}
//           >
//             <i className="ti ti-plus"></i> Assign Bulk Package
//           </button>
//           <button
//             className="btn border bg-white px-3"
//             onClick={() => setShowGuide((prev) => !prev)}
//           >
//             <i className="ti ti-info-circle-filled fs-16"></i> Guide
//           </button>
//         </div>

//         {showGuide && (
//           <div
//             style={{
//               overflow: "hidden",
//               maxHeight: showGuide ? "500px" : "0px",
//               transition: "all 0.4s ease",
//             }}
//           >
//             {/* Top Arrow */}
//             <div className="text-end pe-3">
//               <i
//                 className="ti ti-triangle-inverted-filled"
//                 style={{
//                   fontSize: "22px",
//                   color: "#cfcfcf",
//                   lineHeight: 0,
//                 }}
//               ></i>
//             </div>

//             {/* Guide Box */}
//             <div
//               className="card border-0 shadow-sm"
//               style={{
//                 borderRadius: "4px",
//               }}
//             >
//               <div className="card-body position-relative p-4">
//                 {/* Close Button */}
//                 <div className="position-absolute top-0 end-0 mt-3 me-3">
//                   <i
//                     className="ti ti-x"
//                     style={{
//                       background: "#6c757d",
//                       color: "#fff",
//                       borderRadius: "50%",
//                       width: "20px",
//                       height: "20px",
//                       fontSize: "12px",
//                       cursor: "pointer",
//                       display: "inline-flex",
//                       alignItems: "center",
//                       justifyContent: "center",
//                     }}
//                     onClick={() => setShowGuide(false)}
//                   ></i>
//                 </div>

//                 {/* Heading */}
//                 <h5 className="fw-bold mb-3">Manage Packages</h5>

//                 {/* Paragraph */}
//                 <p className="mb-3 text-muted" style={{ lineHeight: "1.7" }}>
//                   This allows you to give an easy name to each package that you
//                   may want to order. For example within operations you may want
//                   to add a package name as Operations Executive, Operations
//                   Manager. You can also assign the scope of work for that
//                   package. For example you want to conduct 2Employment +
//                   1Address + 1ID Check for the operations executive.
//                 </p>
//               </div>
//             </div>
//           </div>
//         )}

//         {/* <AssignPackage
//           show={assignPackage}
//           handleClose={() => setAssignPackage(false)}
//         /> */}

//         {assignPackage && (
//           <AssignPackage
//             show={assignPackage}
//             handleClose={() => setAssignPackage(false)}
//             onSuccess={loadPackageList}
//           />
//         )}
//         {assignBulkPackage && (
//           <AssignBulkPackage
//             show={assignBulkPackage}
//             handleClose={() => setAssignBulkPackage(false)}
//             onSuccess={loadPackageList}
//           />
//         )}

//         {/* Table  */}
//         {packageLoading ? (
//           <SnipperLoader />
//         ) : (
//           <div className="card mt-3">
//             <Datatable
//               rowKey="sno"
//               dataSource={filteredData}
//               columns={columns}
//               Selection={false}
//               pagination={{
//                 current: pagination.page,
//                 pageSize: pagination.limit,
//                 total,
//               }}
//               handleTableChange={handleTableChange}
//               // pagination={pagination}
//               // handleTableChange={handleTableChange}
//             />
//           </div>
//         )}
//       </div>

//       <Footer />
//     </div>
//   );
// };

// export default Package;
